import { Trans, useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { Project, ProjectListItem } from '@/modules/projects/types/project';

import { useDeleteProjectsBulk } from '../../hooks/use-delete-projects-bulk';

const MAX_VISIBLE_PROJECTS = 5;

interface DeleteProjectsModalProps {
  projectsForDelete: (Project | ProjectListItem)[];
  onSuccess: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * A comprehensive project deletion modal supporting both single and bulk project deletion.
 * Provides clear warnings about permanent data loss with detailed consequence messaging.
 *
 * **Key Features:**
 * - **Dual Mode Operation**: Handles both single and multiple project deletions
 * - **Smart Messaging**: Dynamic content based on single vs. multiple projects
 * - **Comprehensive Warnings**: Detailed list of what data will be permanently lost
 * - **Bulk Operation Support**: Efficiently processes multiple project deletions
 * - **API Integration**: Handles async deletion operations with proper error handling
 * - **Data Consistency**: Invalidates relevant query caches after successful deletion
 * - **Flexible Triggers**: Supports custom triggers or falls back to default destructive button
 * - **Loading States**: Shows proper loading feedback during dangerous operations
 *
 * **Deletion Process:**
 * 1. User selects projects for deletion (single or multiple)
 * 2. Modal shows clear warnings about permanent data loss
 * 3. User confirms understanding of consequences
 * 4. API call permanently deletes projects and all associated data
 * 5. Success invalidates project caches and closes modal
 * 6. Error keeps modal open for retry
 *
 * **Data Permanently Lost:**
 * - Project information and metadata
 * - All time tracking entries
 * - Assignment records and history
 * - Project-related documents and notes
 * - Associated client billing records
 *
 * **Safety Considerations:**
 * - Uses destructive button styling to indicate danger
 * - Requires explicit confirmation
 * - Cannot be undone once confirmed
 * - Recommend archiving instead for data preservation
 *
 * @example
 * ```tsx
 * // Single project deletion
 * <DeleteProjectsModal
 *   projectsForDelete={[selectedProject]}
 *   onSuccess={() => {
 *     toast.success('Project deleted successfully');
 *     navigate('/projects');
 *   }}
 * />
 *
 * // Multiple projects deletion
 * const selectedProjects = [project1, project2, project3];
 * <DeleteProjectsModal
 *   projectsForDelete={selectedProjects}
 *   onSuccess={() => {
 *     toast.success(`${selectedProjects.length} projects deleted`);
 *     clearSelection();
 *     queryClient.invalidateQueries(['projects']);
 *   }}
 * />
 *
 * // With custom trigger button
 * <DeleteProjectsModal
 *   projectsForDelete={[project]}
 *   trigger={
 *     <Button variant="destructive" size="sm">
 *       <Trash2 className="w-4 h-4 mr-2" />
 *       Delete Forever
 *     </Button>
 *   }
 *   onSuccess={() => refetchProjects()}
 * />
 *
 * // Bulk delete with selection management
 * function ProjectBulkActions() {
 *   const [selectedProjects, setSelectedProjects] = useState<Project[]>([]);
 *   const [showDeleteModal, setShowDeleteModal] = useState(false);
 *
 *   const handleBulkDelete = () => {
 *     if (selectedProjects.length === 0) return;
 *     setShowDeleteModal(true);
 *   };
 *
 *   return (
 *     <>
 *       <Button
 *         variant="destructive"
 *         onClick={handleBulkDelete}
 *         disabled={selectedProjects.length === 0}
 *       >
 *         Delete Selected ({selectedProjects.length})
 *       </Button>
 *
 *       <DeleteProjectsModal
 *         projectsForDelete={selectedProjects}
 *         open={showDeleteModal}
 *         onOpen={() => setShowDeleteModal(true)}
 *         onClose={() => setShowDeleteModal(false)}
 *         onSuccess={() => {
 *           setSelectedProjects([]);
 *           setShowDeleteModal(false);
 *           toast.success('Projects deleted successfully');
 *         }}
 *       />
 *     </>
 *   );
 * }
 *
 * // In project management context menu
 * <ContextMenu>
 *   <ContextMenuContent>
 *     <ContextMenuItem asChild>
 *       <DeleteProjectsModal
 *         projectsForDelete={[project]}
 *         trigger={
 *           <span className="flex items-center text-destructive">
 *             <Trash2 className="w-4 h-4 mr-2" />
 *             Delete Project
 *           </span>
 *         }
 *         onSuccess={() => {
 *           toast.success('Project deleted');
 *           closeContextMenu();
 *         }}
 *       />
 *     </ContextMenuItem>
 *   </ContextMenuContent>
 * </ContextMenu>
 *
 * // With confirmation workflow and alternative actions
 * function ProjectDeletionFlow() {
 *   const [selectedProject, setSelectedProject] = useState<Project | null>(null);
 *   const [showArchiveOption, setShowArchiveOption] = useState(true);
 *
 *   const handleDeleteRequest = (project: Project) => {
 *     setSelectedProject(project);
 *     // Show archive option first as safer alternative
 *   };
 *
 *   return (
 *     <div className="space-y-4">
 *       {showArchiveOption && selectedProject && (
 *         <Alert>
 *           <AlertDescription>
 *             Consider archiving instead of deleting to preserve data.
 *             <div className="flex gap-2 mt-2">
 *               <ArchiveProjectModal
 *                 project={selectedProject}
 *                 trigger={<Button variant="outline" size="sm">Archive Instead</Button>}
 *                 onSuccess={() => {
 *                   setSelectedProject(null);
 *                   setShowArchiveOption(false);
 *                 }}
 *               />
 *               <Button
 *                 variant="destructive"
 *                 size="sm"
 *                 onClick={() => setShowArchiveOption(false)}
 *               >
 *                 Delete Permanently
 *               </Button>
 *             </div>
 *           </AlertDescription>
 *         </Alert>
 *       )}
 *
 *       {!showArchiveOption && selectedProject && (
 *         <DeleteProjectsModal
 *           projectsForDelete={[selectedProject]}
 *           onSuccess={() => {
 *             analytics.track('project_deleted', {
 *               projectId: selectedProject.id,
 *               deletionReason: 'user_confirmed',
 *               hadAlternativeOption: true
 *             });
 *             setSelectedProject(null);
 *             toast.success('Project deleted permanently');
 *           }}
 *         />
 *       )}
 *     </div>
 *   );
 * }
 *
 * // With additional safety checks
 * function SafeDeleteProjectsModal({
 *   projects,
 *   onSuccess
 * }: {
 *   projects: Project[];
 *   onSuccess: () => void;
 * }) {
 *   const hasActiveAssignments = projects.some(p => p.assignmentCount > 0);
 *   const hasRecentActivity = projects.some(p =>
 *     p.lastActivity && Date.now() - p.lastActivity.getTime() < 7 * 24 * 60 * 60 * 1000
 *   );
 *
 *   if (hasActiveAssignments || hasRecentActivity) {
 *     return (
 *       <Alert variant="destructive">
 *         <AlertDescription>
 *           Cannot delete projects with active assignments or recent activity.
 *           Please archive them instead.
 *         </AlertDescription>
 *       </Alert>
 *     );
 *   }
 *
 *   return (
 *     <DeleteProjectsModal
 *       projectsForDelete={projects}
 *       onSuccess={onSuccess}
 *     />
 *   );
 * }
 * ```
 */
export function DeleteProjectsModal({
  projectsForDelete,
  onSuccess,
  trigger,
  open,
  onOpen,
  onClose,
}: DeleteProjectsModalProps) {
  const { t } = useTranslation(['authenticated.projects', 'common']);

  const projectNames = projectsForDelete.map(p => p.name ?? '');
  const visibleProjectNames = projectNames.slice(0, MAX_VISIBLE_PROJECTS);
  const remainingProjectNames = projectNames.slice(MAX_VISIBLE_PROJECTS);
  const remainingCount = remainingProjectNames.length;

  const projectIds = projectsForDelete.map(p => p.id);

  const deleteProjectsMutation = useDeleteProjectsBulk({ onSuccess });

  const handleDelete = async (closeModal: () => void) => {
    try {
      await deleteProjectsMutation.mutateAsync(projectIds);
      closeModal();
    } catch (error) {
      // Error handled by hook, keep modal open for retry
    }
  };

  const description = (
    <div>
      <Trans
        i18nKey="authenticated.projects:delete.deleteProjectsMessage"
        count={projectsForDelete.length}
        values={{
          projects: visibleProjectNames.join(', '),
          project: visibleProjectNames.join(', '),
        }}
        components={{ b: <b /> }}
      />
      {remainingCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help font-bold">
              {t('authenticated.projects:delete.andMoreProjects', { count: remainingCount })}
            </span>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="max-w-md">
              <p>{remainingProjectNames.join(', ')}</p>
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      )}
    </div>
  );

  const additionalContent = (
    <div className="text-sm text-muted-foreground">
      <p className="font-bold">{t('authenticated.projects:delete.attention')}</p>
      <ul className="list-disc pl-4">
        <li>{t('authenticated.projects:delete.projectInformation')}</li>
        <li>{t('authenticated.projects:delete.assignments')}</li>
      </ul>
    </div>
  );

  return (
    <ConfirmationModal
      trigger={trigger}
      title={t('authenticated.projects:delete.deleteProjects', {
        count: projectsForDelete.length,
      })}
      message={description}
      confirmText={
        deleteProjectsMutation.isPending
          ? t('authenticated.projects:delete.deleting')
          : t('authenticated.projects:delete.deleteProjectsButton', {
              count: projectsForDelete.length,
            })
      }
      confirmVariant="destructive"
      onConfirm={handleDelete}
      isLoading={deleteProjectsMutation.isPending}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
    >
      {additionalContent}
    </ConfirmationModal>
  );
}
